/* Interior module constants + seed data + lead generator.

   Customer entry point is the advisor card on project pages (variant
   "interior"). It captures name, mobile, optional separate WhatsApp,
   one of four "intent" options, and an optional message. The admin
   panel adds operational metadata once leads reach the team: status,
   internal note, assignee.

   Pipeline doesn't follow property statuses (no Token / Booked /
   Re-visit). The flow is closer to a design-agency funnel:
   New → Contacted → Consult set → Quote sent → In progress → Won / Lost.
   Kept short on purpose; teams can extend as needed. */

export const INTERIOR_INTENTS = [
  { value: "design-consult", label: "Free design consultation" },
  { value: "cost-estimate",  label: "Get a cost estimate" },
  { value: "see-portfolio",  label: "See past projects" },
  { value: "modular",        label: "Modular kitchen only" },
];
export const INTERIOR_INTENT_LABEL = Object.fromEntries(
  INTERIOR_INTENTS.map((o) => [o.value, o.label])
);

export const INTERIOR_LEAD_STATUSES = [
  "new", "contacted", "consult_scheduled", "quote_sent", "in_progress", "won", "lost",
];
export const INTERIOR_LEAD_STATUS_META = {
  new:               { label: "New",         cls: "bg-blue-50 text-blue-700 ring-blue-600/20" },
  contacted:         { label: "Contacted",   cls: "bg-slate-100 text-slate-700 ring-slate-500/20" },
  consult_scheduled: { label: "Consult Set", cls: "bg-violet-50 text-violet-700 ring-violet-600/20" },
  quote_sent:        { label: "Quote Sent",  cls: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  in_progress:       { label: "In Progress", cls: "bg-teal-50 text-teal-700 ring-teal-600/20" },
  won:               { label: "Won",         cls: "bg-green-50 text-green-700 ring-green-600/20" },
  lost:              { label: "Lost",        cls: "bg-red-50 text-red-700 ring-red-600/20" },
};

/* Services rendered as the grid on the customer interior page. Editable
   in the admin Interior → Page & Services screen. */
export const INTERIOR_SERVICES = [
  { id: "is1", name: "Modular Kitchen" },
  { id: "is2", name: "Living Room" },
  { id: "is3", name: "Master Bedroom" },
  { id: "is4", name: "Wardrobe" },
  { id: "is5", name: "False Ceiling" },
  { id: "is6", name: "Full Home Interiors" },
];

/* Three fixed package tiers. Slug is the stable identity; name/tagline/
   price/features are editable. The "popular" flag drives the badge on
   the customer site's pricing strip. */
export const INTERIOR_PACKAGES = [
  {
    slug: "essentials", name: "Essentials",
    tagline: "Perfect for compact homes", price: "₹1,99,000", popular: false,
    features: ["Modular Kitchen", "Wardrobes in 1 Bedroom", "Basic False Ceiling", "2D Design & Consultation"],
  },
  {
    slug: "premium", name: "Premium",
    tagline: "Perfect blend of style & comfort", price: "₹3,49,000", popular: true,
    features: ["Modular Kitchen", "Wardrobes in 2 Bedrooms", "False Ceiling with Lights", "3D Design & Consultation", "Premium Materials"],
  },
  {
    slug: "luxury", name: "Luxury",
    tagline: "For a luxurious living experience", price: "₹5,99,000", popular: false,
    features: ["Modular Kitchen (Premium)", "Wardrobes in All Bedrooms", "Designer False Ceiling", "3D Design & Consultation", "Premium Materials & Decor"],
  },
];

export const PORTFOLIO_CATEGORIES = ["Living Room", "Kitchen", "Bedroom", "Full Home"];
export const INTERIOR_PORTFOLIO = [
  { id: "ip1", title: "Modern Living Room",  category: "Living Room", location: "3 BHK Apartment · Pune" },
  { id: "ip2", title: "Modular Kitchen",     category: "Kitchen",     location: "2 BHK Apartment · Mumbai" },
  { id: "ip3", title: "Master Bedroom",      category: "Bedroom",     location: "4 BHK Apartment · Bangalore" },
  { id: "ip4", title: "Full Home Interior",  category: "Full Home",   location: "3 BHK Apartment · Hyderabad" },
];

/* Seed generator for interior leads. Mirrors genLeads's shape: first 3
   leads are "today" so the Today's Leads KPI fires on first render,
   the rest spread across the past two weeks. Name pool is intentionally
   different from the property lead pool so demos don't look duplicated.

   Status biased toward early-pipeline stages so the screen looks like
   an active funnel (most leads being worked) rather than a graveyard
   of closed deals — only every 5th lead lands in won/lost. */
export function genInteriorLeads(n) {
  const names = [
    "Aanya Sharma", "Devansh Pillai", "Kritika Joshi", "Vishal Bhat",
    "Tanvi Mehta", "Rohit Kale", "Maya Iyer", "Saurabh Phadke",
    "Ishita Kulkarni", "Adit Apte", "Sneha Joglekar", "Yash Patil",
    "Mira Chavan", "Tushar Karve",
  ];
  // A handful of realistic messages so the table reads like real
  // customer enquiries. Empty entries simulate the (very common) case
  // where the user submits without filling the optional textarea.
  const messages = [
    "Open kitchen with island, modular wardrobes for 2 BHK",
    "End-to-end interiors for new 3 BHK in Baner — possession in 4 months",
    "",
    "Master bedroom + kid's room only, ballpark 4–5 L",
    "Just modular kitchen for now, want to see portfolio first",
    "False ceiling + foyer makeover for ready-possession flat",
    "",
    "Looking for German hardware brands — Hettich preferred",
  ];
  const intents = INTERIOR_INTENTS.map((o) => o.value);
  const out = [];
  for (let i = 0; i < n; i++) {
    const created = i < 3
      ? new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 18)
      : new Date(Date.now() - (1 + Math.random() * 13) * 1000 * 60 * 60 * 24);
    const mobile10 = String(9000000000 + Math.floor(Math.random() * 999999999)).slice(0, 10);
    const wantsSeparateWA = Math.random() < 0.15;
    const wa10 = wantsSeparateWA ? String(9000000000 + Math.floor(Math.random() * 999999999)).slice(0, 10) : "";
    let status;
    if (i % 5 === 4) status = i % 10 === 4 ? "won" : "lost";
    else status = INTERIOR_LEAD_STATUSES[Math.floor(Math.random() * 5)];
    out.push({
      id: "IL" + (1000 + i),
      name: names[i % names.length],
      mobile: mobile10,
      whatsapp: wa10,
      intent: intents[Math.floor(Math.random() * intents.length)],
      message: messages[Math.floor(Math.random() * messages.length)],
      status,
      source: "Website",
      createdAt: created,
      lastActivity: new Date(created.getTime() + Math.random() * 1000 * 60 * 60 * 48),
      note: "",
      assignedTo: null,
    });
  }
  return out;
}

/* Wrap seed generation in a try/catch so a module-level evaluation
   error can't bring down the entire admin panel. In the worst case
   the screen opens with no seed data — InteriorLeadsView handles that
   gracefully via the empty-state message. */
let _INTERIOR_LEADS;
try {
  _INTERIOR_LEADS = genInteriorLeads(14);
} catch (e) {
  console.error("Failed to seed interior leads:", e);
  _INTERIOR_LEADS = [];
}
export const INTERIOR_LEADS = _INTERIOR_LEADS;
